/**
 * A aba TIMES da Equipe (H129, spec 24) — um cartão por fila.
 *
 * O owner (01/11): *"não estou vendo aonde que eu vinculo a equipe para cada supervisor"*. É aqui: cada time mostra os
 * RESPONSÁVEIS (o supervisor, ADR-0022), os membros com presença ao vivo e carga × limite, a IA como membro, os números
 * de agora e as ações. Time = FILA (decisão do owner): criar time é criar fila, responsáveis e membros num diálogo só.
 * Com uma fila só, a aba já abre nela, inteira.
 *
 * COM VÁRIOS TIMES, UMA LINHA POR TIME (owner, 01/11: *"eles vão vir sempre abertos? e se eu tiver 20 grupos?"*): os
 * cartões abertos com a lista inteira de membros não escalavam — vinte times viravam uma parede. Cada time é uma linha
 * fechada (nome, responsáveis, presença resumida, números de agora), os com problema primeiro; o time abre numa Gaveta
 * com o cartão inteiro. Busca e o filtro "com gente esperando" aparecem a partir de seis times.
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Botao, Ladrilho, Retrato, useSessao } from "@hugi/ui";
import { Dialogo, Gaveta, Selo, Vazio } from "@hugi/ui/kit";
import { AlarmClock, Bot, ChevronRight, ExternalLink, Hourglass, Settings2, TriangleAlert, Users } from "lucide-react";
import {
  criarTime,
  definirResponsaveis,
  desvincularDaFila,
  vincularAFila,
  type AtendenteNaLista,
  type TimeNaLista,
} from "./dados.js";
import { Carga, MenuDoSupervisor, PontoNaTela, SeloDePresenca, duracaoCurta } from "./equipe-comum.js";

const ATENDE: Readonly<Record<string, string>> = {
  sempre: "atende sempre",
  fora_do_horario: "cobre fora do horário",
  nunca: "desligado",
};

export function AbaTimes(props: {
  readonly times: readonly TimeNaLista[];
  readonly pessoas: readonly AtendenteNaLista[];
  readonly motivos: readonly string[];
  readonly aoCriar?: (() => void) | undefined;
}) {
  const [ajustando, setAjustando] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  const time = ajustando ? (props.times.find((t) => t.id === ajustando) ?? null) : null;
  const detalhe = aberto ? (props.times.find((t) => t.id === aberto) ?? null) : null;

  if (props.times.length === 0) {
    return (
      <Vazio {...(props.aoCriar ? { acao: <Botao onClick={props.aoCriar}>Novo time</Botao> } : {})}>
        Nenhum time ainda. Um time é uma fila: quem atende nela e quem a supervisiona.
      </Vazio>
    );
  }

  const ajustes = (
    <Gaveta aberta={time !== null} aoFechar={() => setAjustando(null)} titulo={time ? `Ajustar ${time.nome}` : "Ajustar time"}>
      {time ? <AjustesDoTime key={time.id} time={time} pessoas={props.pessoas} /> : null}
    </Gaveta>
  );

  if (props.times.length === 1) {
    return (
      <>
        <ul className="hugi-times is-um" aria-label="Times">
          <CartaoDoTime time={props.times[0]!} pessoas={props.pessoas} motivos={props.motivos} aoAjustar={() => setAjustando(props.times[0]!.id)} />
        </ul>
        {ajustes}
      </>
    );
  }

  return (
    <>
      <ListaDeTimes times={props.times} aoAbrir={setAberto} aoDefinirLider={setAjustando} />
      <Gaveta aberta={detalhe !== null} aoFechar={() => setAberto(null)} titulo={detalhe ? `Time ${detalhe.nome}` : "Time"} largura="larga">
        {detalhe ? (
          <ul className="hugi-times is-um is-na-gaveta" aria-label="Time">
            <CartaoDoTime
              key={detalhe.id}
              time={detalhe}
              pessoas={props.pessoas}
              motivos={props.motivos}
              aoAjustar={() => {
                setAberto(null);
                setAjustando(detalhe.id);
              }}
            />
          </ul>
        ) : null}
      </Gaveta>
      {ajustes}
    </>
  );
}

/** Pede atenção primeiro: fora do prazo, depois quem espera há mais tempo, depois quantos esperam. */
export function ordemDosTimes(a: TimeNaLista, b: TimeNaLista): number {
  if (b.agora.foraDoPrazo !== a.agora.foraDoPrazo) return b.agora.foraDoPrazo - a.agora.foraDoPrazo;
  const ta = a.agora.maisAntigaEm ? new Date(a.agora.maisAntigaEm).getTime() : Infinity;
  const tb = b.agora.maisAntigaEm ? new Date(b.agora.maisAntigaEm).getTime() : Infinity;
  if (ta !== tb) return ta - tb;
  if (b.agora.esperando !== a.agora.esperando) return b.agora.esperando - a.agora.esperando;
  return a.nome.localeCompare(b.nome, "pt-BR");
}

const MUITOS_TIMES = 6;

function ListaDeTimes(props: { readonly times: readonly TimeNaLista[]; readonly aoAbrir: (id: string) => void; readonly aoDefinirLider: (id: string) => void }) {
  const [busca, setBusca] = useState("");
  const [soEsperando, setSoEsperando] = useState(false);
  const muitos = props.times.length >= MUITOS_TIMES;
  const termo = busca.trim().toLocaleLowerCase("pt-BR");
  const visiveis = [...props.times]
    .filter((t) => !soEsperando || t.agora.esperando > 0)
    .filter((t) => !termo || t.nome.toLocaleLowerCase("pt-BR").includes(termo) || t.membros.some((m) => (m.nome ?? "").toLocaleLowerCase("pt-BR").includes(termo)))
    .sort(ordemDosTimes);

  return (
    <div className="hugi-times-lista">
      {muitos ? (
        <div className="hugi-times-ferramentas">
          <label className="v2-lista-busca">
            <span className="v2-sr">Buscar time ou pessoa</span>
            <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar time ou pessoa" />
          </label>
          <button type="button" className={`v2-botao is-pequeno${soEsperando ? " is-ligado" : ""}`} aria-pressed={soEsperando} onClick={() => setSoEsperando((v) => !v)}>
            Com gente esperando
          </button>
          <span className="hugi-equipe-fraco">{visiveis.length === 1 ? "1 time" : `${visiveis.length} times`}</span>
        </div>
      ) : null}
      {visiveis.length === 0 ? (
        <Vazio>Nenhum time com esse filtro.</Vazio>
      ) : (
        <ul className="hugi-times-linhas" aria-label="Times">
          {visiveis.map((t) => (
            <LinhaDoTime key={t.id} time={t} aoAbrir={() => props.aoAbrir(t.id)} aoDefinirLider={() => props.aoDefinirLider(t.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function LinhaDoTime(props: { readonly time: TimeNaLista; readonly aoAbrir: () => void; readonly aoDefinirLider: () => void }) {
  const { time: t } = props;
  const sessao = useSessao();
  const responsaveis = t.membros.filter((m) => m.responsavel);
  const contar = (e: string) => t.membros.filter((m) => (m.disponibilidade?.estado ?? "disponivel") === e).length;
  const maisAntiga = t.agora.maisAntigaEm ? Math.floor((Date.now() - new Date(t.agora.maisAntigaEm).getTime()) / 1000) : null;
  return (
    <li className={`hugi-time-linha${t.agora.foraDoPrazo > 0 ? " is-alerta" : ""}`}>
      <button type="button" onClick={props.aoAbrir} aria-label={`Abrir o time ${t.nome}`}>
        <Ladrilho tom="eq">
          <Users size={15} />
        </Ladrilho>
        <span className="hugi-time-linha-nome">
          <strong>{t.nome}</strong>
          <small>
            {responsaveis.length === 0 ? (
              <span className="hugi-equipe-atencao">sem líder</span>
            ) : (
              `Líder: ${responsaveis.map((r) => r.nome ?? "Sem nome").join(", ")}`
            )}
          </small>
        </span>
        <span className="hugi-time-linha-gente" title="Disponíveis · em pausa · offline">
          <span className="is-disponivel">{contar("disponivel")}</span>
          <span className="is-pausa">{contar("pausa")}</span>
          <span className="is-offline">{t.membros.length - contar("disponivel") - contar("pausa")}</span>
          {t.ia ? <Bot size={14} aria-label="com assistente" /> : null}
        </span>
        <span className="hugi-time-linha-numero" title="Esperando">
          <Hourglass size={13} aria-hidden="true" />
          {t.agora.esperando}
        </span>
        <span className="hugi-time-linha-numero" title="A mais antiga">
          <AlarmClock size={13} aria-hidden="true" />
          {maisAntiga === null ? "—" : duracaoCurta(maisAntiga)}
        </span>
        <span className={`hugi-time-linha-numero${t.agora.foraDoPrazo > 0 ? " is-alerta" : ""}`} title="Fora do prazo">
          <TriangleAlert size={13} aria-hidden="true" />
          {t.agora.foraDoPrazo}
        </span>
        <ChevronRight size={16} aria-hidden="true" className="hugi-time-linha-seta" />
      </button>
      {/* "Definir líder" na própria linha (owner, 01/11: "e como eu defino quem é o líder do time?"): o caminho era
          abrir o time, achar a engrenagem e marcar na gaveta — quatro cliques e nenhuma pista. */}
      {responsaveis.length === 0 && sessao.permissoes.includes("manage_team") ? (
        <button type="button" className="v2-botao is-pequeno hugi-time-linha-lider" onClick={props.aoDefinirLider}>
          Definir líder
        </button>
      ) : null}
    </li>
  );
}

function CartaoDoTime(props: {
  readonly time: TimeNaLista;
  readonly pessoas: readonly AtendenteNaLista[];
  readonly motivos: readonly string[];
  readonly aoAjustar: () => void;
}) {
  const { time: t } = props;
  const sessao = useSessao();
  const administra = sessao.permissoes.includes("manage_team");
  const souResponsavel = t.membros.some((m) => m.responsavel && m.atendenteId === sessao.usuario.id);
  // As ações sobre as pessoas: quem administra a equipe, ou é responsável por ESTE time (o servidor confere de novo).
  const supervisiona = administra || souResponsavel;
  const podeAjustar = administra || sessao.permissoes.includes("manage_catalog");
  const responsaveis = t.membros.filter((m) => m.responsavel);
  const maisAntiga = t.agora.maisAntigaEm ? Math.floor((Date.now() - new Date(t.agora.maisAntigaEm).getTime()) / 1000) : null;
  const destinos = t.membros.map((m) => ({ id: m.atendenteId, nome: m.nome, disponibilidade: m.disponibilidade }));

  return (
    <li className="v2-cartao hugi-time" aria-label={`Time ${t.nome}`}>
      <header className="hugi-time-cabeca">
        <Ladrilho tom="eq">
          <Users size={16} />
        </Ladrilho>
        <h3>{t.nome}</h3>
        <span className="hugi-time-acoes">
          <a className="v2-botao" href={`/atender?time&fila=${encodeURIComponent(t.id)}`}>
            <ExternalLink size={14} aria-hidden="true" />
            Painel do time
          </a>
          {podeAjustar ? (
            <button type="button" className="v2-botao-icone" onClick={props.aoAjustar} aria-label={`Ajustar o time ${t.nome}`} title="Ajustar">
              <Settings2 size={15} aria-hidden="true" />
            </button>
          ) : null}
        </span>
      </header>

      <dl className="hugi-time-numeros">
        <Numero icone={<Hourglass size={14} />} rotulo="Esperando" valor={t.agora.esperando} />
        <Numero icone={<AlarmClock size={14} />} rotulo="A mais antiga" valor={maisAntiga === null ? "—" : duracaoCurta(maisAntiga)} />
        <Numero icone={<TriangleAlert size={14} />} rotulo="Fora do prazo" valor={t.agora.foraDoPrazo} alerta={t.agora.foraDoPrazo > 0} />
      </dl>

      <p className="hugi-time-responsaveis">
        <span className="hugi-equipe-fraco">Líder</span>
        {responsaveis.length === 0 ? (
          <span className="hugi-equipe-atencao">ninguém</span>
        ) : (
          <>
            {/* Três à vista e o resto contado: o cartão tem a altura do conteúdo, e uma lista longa o esticaria. */}
            {responsaveis.slice(0, 3).map((r) => (
              <span key={r.atendenteId} className="hugi-time-chip">
                <Retrato nome={r.nome ?? "?"} />
                {r.nome ?? "Sem nome"}
              </span>
            ))}
            {responsaveis.length > 3 ? (
              <span className="hugi-equipe-fraco" title={responsaveis.slice(3).map((r) => r.nome ?? "Sem nome").join(", ")}>
                +{responsaveis.length - 3}
              </span>
            ) : null}
          </>
        )}
      </p>

      <ul className="hugi-time-membros" aria-label={`Quem está no time ${t.nome}`}>
        {t.ia ? (
          <li className="hugi-time-membro is-ia">
            <Ladrilho tom="ia">
              <Bot size={15} />
            </Ladrilho>
            <span className="hugi-time-membro-nome">
              <strong>{t.ia.nome}</strong>
              <small>{ATENDE[t.ia.atende] ?? t.ia.atende}</small>
            </span>
            <span className="hugi-time-ia-numeros" title="Conversas com o assistente agora · passadas para pessoas hoje">
              segura {t.ia.segurando} · passou {t.ia.passouHoje} hoje
            </span>
          </li>
        ) : null}
        {t.membros.length === 0 ? (
          <li className="hugi-equipe-fraco hugi-time-vazio">Ninguém neste time ainda.</li>
        ) : (
          t.membros.map((m) => (
            <li key={m.atendenteId} className="hugi-time-membro">
              <span className="hugi-time-avatar">
                <Retrato nome={m.nome ?? "?"} />
                <PontoNaTela naTela={m.naTela} />
              </span>
              <span className="hugi-time-membro-nome">
                <strong title={m.nome ?? undefined}>{m.nome ?? "Sem nome"}</strong>
                {/* A presença embaixo do nome: ao lado dele, o nome cortava no cartão de 360 px. */}
                <span className="hugi-time-membro-estado">
                  <SeloDePresenca disponibilidade={m.disponibilidade} />
                  {m.responsavel ? <small>{m.naDistribuicao ? "responsável" : "responsável · só supervisiona"}</small> : null}
                </span>
              </span>
              <Carga abertos={m.abertos} limite={m.limite} />
              {supervisiona && m.atendenteId !== sessao.usuario.id ? (
                <MenuDoSupervisor
                  alvo={{ atendenteId: m.atendenteId, nome: m.nome, abertos: m.abertos, disponibilidade: m.disponibilidade }}
                  podeRedistribuir={sessao.permissoes.includes("reassign_conversation")}
                  destinos={destinos}
                  motivos={props.motivos}
                />
              ) : (
                <span className="hugi-time-sem-menu" aria-hidden="true" />
              )}
            </li>
          ))
        )}
      </ul>
    </li>
  );
}

function Numero(props: { readonly icone: ReactNode; readonly rotulo: string; readonly valor: ReactNode; readonly alerta?: boolean }) {
  return (
    <div className={props.alerta ? "is-alerta" : undefined}>
      <dt>
        <span aria-hidden="true">{props.icone}</span>
        {props.rotulo}
      </dt>
      <dd>{props.valor}</dd>
    </div>
  );
}

/**
 * Ajustar o time, na gaveta: os responsáveis (`manage_team`; só quem o Cert4All deixa supervisionar), se o responsável
 * entra na distribuição, e pôr/tirar pessoas (`manage_catalog`, a mesma rota de antes).
 */
function AjustesDoTime(props: { readonly time: TimeNaLista; readonly pessoas: readonly AtendenteNaLista[] }) {
  const { time: t } = props;
  const sessao = useSessao();
  const cliente = useQueryClient();
  const administra = sessao.permissoes.includes("manage_team");
  const arruma = sessao.permissoes.includes("manage_catalog");
  const [responsaveis, setResponsaveis] = useState<readonly string[]>(t.membros.filter((m) => m.responsavel).map((m) => m.atendenteId));
  const [distribui, setDistribui] = useState(t.responsavelDistribui);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const ativos = props.pessoas.filter((p) => p.ativo);
  const fora = ativos.filter((p) => !t.membros.some((m) => m.atendenteId === p.atendenteId));

  const agir = async (acao: () => Promise<{ ok: boolean; falha?: { erro: { mensagem?: string } } }>, ok: string) => {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    const r = await acao();
    setOcupado(false);
    if (!r.ok) setErro(r.falha?.erro.mensagem ?? "Não deu para salvar.");
    else setAviso(ok);
  };

  return (
    <div className="hugi-equipe-form">
      <fieldset className="hugi-equipe-grupo">
        <legend>Líder do time</legend>
        {/* A consequência em uma linha: ser responsável RESTRINGE quem não administra (ADR-0022). */}
        <small className="hugi-equipe-fraco">O líder vê e conduz só os times que lidera.</small>
        {ativos.length > 0 && ativos.every((p) => p.podeSupervisionar === false) ? (
          // Ninguém elegível: o papel vem do Cert4All, e a tela diz onde mudar em vez de mostrar caixas apagadas.
          <p className="hugi-equipe-atencao">Ninguém aqui tem perfil de Supervisor ou Administrador no Cert4All. Mude o perfil da pessoa lá para poder escolhê-la.</p>
        ) : null}
        <ul className="hugi-equipe-escolhas">
          {ativos.map((p) => {
            const nao = p.podeSupervisionar === false;
            return (
              <li key={p.atendenteId}>
                <label title={nao ? "O perfil dela no Cert4All não supervisiona" : undefined}>
                  <input
                    type="checkbox"
                    disabled={!administra || nao || ocupado}
                    checked={responsaveis.includes(p.atendenteId)}
                    onChange={(e) =>
                      setResponsaveis(e.target.checked ? [...responsaveis, p.atendenteId] : responsaveis.filter((x) => x !== p.atendenteId))
                    }
                  />
                  {p.nome ?? "Sem nome"}
                  {nao ? <span className="hugi-equipe-fraco"> · perfil Atendente (não pode liderar)</span> : null}
                </label>
              </li>
            );
          })}
        </ul>
        <label className="hugi-equipe-linha">
          <input type="checkbox" checked={distribui} disabled={!administra || ocupado} onChange={(e) => setDistribui(e.target.checked)} />
          O líder também atende (recebe conversa do time)
        </label>
        {administra ? (
          <Botao onClick={() => void agir(() => definirResponsaveis(cliente, t.id, responsaveis, distribui), "Líder salvo.")} desabilitado={ocupado}>
            Salvar líder
          </Botao>
        ) : (
          <small className="hugi-equipe-fraco">Trocar responsável é com quem administra a equipe.</small>
        )}
      </fieldset>

      <fieldset className="hugi-equipe-grupo">
        <legend>Quem atende</legend>
        <ul className="hugi-equipe-escolhas">
          {t.membros.map((m) => (
            <li key={m.atendenteId} className="hugi-equipe-linha">
              <span>{m.nome ?? "Sem nome"}</span>
              {m.responsavel ? <Selo tom="info">responsável</Selo> : null}
              {arruma && (!m.responsavel || administra) ? (
                <button
                  type="button"
                  className="v2-link"
                  disabled={ocupado}
                  onClick={() => void agir(() => desvincularDaFila(cliente, t.id, m.atendenteId), `${m.nome ?? "A pessoa"} saiu do time.`)}
                  aria-label={`Tirar ${m.nome ?? m.atendenteId} do time ${t.nome}`}
                >
                  tirar
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {arruma && fora.length > 0 ? (
          <select
            value=""
            disabled={ocupado}
            aria-label={`Pôr alguém no time ${t.nome}`}
            onChange={(e) => {
              const id = e.target.value;
              e.target.value = "";
              const p = fora.find((x) => x.atendenteId === id);
              if (id) void agir(() => vincularAFila(cliente, t.id, id), `${p?.nome ?? "A pessoa"} entrou no time.`);
            }}
          >
            <option value="">+ pôr pessoa</option>
            {fora.map((p) => (
              <option key={p.atendenteId} value={p.atendenteId}>
                {p.nome ?? "Sem nome"}
              </option>
            ))}
          </select>
        ) : null}
      </fieldset>
      {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}
      {aviso ? <p role="status" className="hugi-equipe-ok">{aviso}</p> : null}
    </div>
  );
}

/** Novo time: a fila, os responsáveis e os membros num passo só (`POST /api/painel/equipe/times`). */
export function DialogoDoNovoTime(props: { readonly aberto: boolean; readonly aoFechar: () => void; readonly pessoas: readonly AtendenteNaLista[] }) {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const administra = sessao.permissoes.includes("manage_team");
  const [nome, setNome] = useState("");
  const [responsaveis, setResponsaveis] = useState<readonly string[]>([]);
  const [membros, setMembros] = useState<readonly string[]>([]);
  const [distribui, setDistribui] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ativos = props.pessoas.filter((p) => p.ativo);
  const alternar = (lista: readonly string[], id: string, ligado: boolean) => (ligado ? [...lista, id] : lista.filter((x) => x !== id));

  const criar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await criarTime(cliente, { nome: nome.trim(), responsaveis, membros, responsavelDistribui: distribui });
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não deu para criar o time.");
      return;
    }
    setNome("");
    setResponsaveis([]);
    setMembros([]);
    props.aoFechar();
  };

  return (
    <Dialogo aberto={props.aberto} aoFechar={props.aoFechar} titulo="Novo time">
      <form
        className="hugi-equipe-form"
        onSubmit={(e) => {
          e.preventDefault();
          void criar();
        }}
      >
        <label className="hugi-equipe-campo">
          <span>Nome</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Vendas, Suporte, Recepção…" aria-label="Nome do time" required />
        </label>
        <div className="hugi-equipe-colunas">
          <fieldset className="hugi-equipe-grupo">
            <legend>Responsável</legend>
            {administra ? (
              <ul className="hugi-equipe-escolhas">
                {ativos.map((p) => (
                  <li key={p.atendenteId}>
                    <label title={p.podeSupervisionar === false ? "O perfil dela no Cert4All não supervisiona" : undefined}>
                      <input
                        type="checkbox"
                        disabled={p.podeSupervisionar === false}
                        checked={responsaveis.includes(p.atendenteId)}
                        onChange={(e) => setResponsaveis(alternar(responsaveis, p.atendenteId, e.target.checked))}
                      />
                      {p.nome ?? "Sem nome"}
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <small className="hugi-equipe-fraco">Escolher responsável é com quem administra a equipe.</small>
            )}
          </fieldset>
          <fieldset className="hugi-equipe-grupo">
            <legend>Quem atende</legend>
            <ul className="hugi-equipe-escolhas">
              {ativos
                .filter((p) => !responsaveis.includes(p.atendenteId))
                .map((p) => (
                  <li key={p.atendenteId}>
                    <label>
                      <input type="checkbox" checked={membros.includes(p.atendenteId)} onChange={(e) => setMembros(alternar(membros, p.atendenteId, e.target.checked))} />
                      {p.nome ?? "Sem nome"}
                    </label>
                  </li>
                ))}
            </ul>
          </fieldset>
        </div>
        {administra ? (
          <label className="hugi-equipe-linha">
            <input type="checkbox" checked={distribui} onChange={(e) => setDistribui(e.target.checked)} />
            O responsável também atende
          </label>
        ) : null}
        {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}
        <div className="hugi-equipe-acoes">
          <Botao onClick={() => void criar()} desabilitado={ocupado || !nome.trim()} motivoDeDesabilitar={nome.trim() ? "Criando…" : "Dê um nome ao time."}>
            {ocupado ? "Criando…" : "Criar time"}
          </Botao>
          <Botao tipo="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}
